import { getActiveLang } from '@openclassrooms/translations';
import {
  getCachedCurrentUser,
  removeCachedCurrentUser,
  clearCachedCurrentUser,
} from '@openclassrooms/data-connect-web/users';

import { deleteCookie, getCookies } from '@openclassrooms/utils/cookies/services';

import scopes from './constants/scopes';
import sdkService from './services/sdk';

type SdkService = typeof sdkService;

declare global {
  interface Window {
    sdk: SdkService;
  }
}

export const sdk: SdkService = sdkService;

let pendingInitSdkPromise: Promise<void> | null = null;

export default async function initAPIClients(config: {
  urls: {
    proxy?: string;
    root: string;
    authorize: string;
  };
  anonymous: {
    client_id: string;
    client_secret: string;
  };
  logged: {
    client_id: string;
  };
  userId?: number;
}): Promise<void> {
  window.sdk = sdkService;

  if (sdkService.isInit()) {
    return undefined;
  }

  if (pendingInitSdkPromise) {
    return pendingInitSdkPromise;
  }

  if (!config) {
    return undefined;
  }

  const { urls, anonymous, logged, userId } = config;

  const sdkOptions = {
    language: getActiveLang(),
    rootUrl: urls.proxy || urls.root,
    authorizeUrl: urls.authorize,
    anonymous: {
      clientId: anonymous.client_id,
      clientSecret: anonymous.client_secret,
    },
    logged: {
      clientId: logged.client_id,
    },
  };

  sdkService.init(sdkOptions);

  const cachedCurrentUserWithTimeExpiry = await getCachedCurrentUser();
  const cachedCurrentUser = cachedCurrentUserWithTimeExpiry?.user ?? null;

  const cookies = getCookies();

  const COOKIES_KEY = {
    ACCESS_TOKEN: 'access_token',
    ACCESS_TOKEN_EXPIRES_IN: 'access_token_expires_in',
  };

  if (userId || cookies[COOKIES_KEY.ACCESS_TOKEN]) {
    if (!cachedCurrentUser) {
      // User is supposed to be connected but our local cache is empty: let's remove it totally.
      // It'll be filled again on next request to current user
      await removeCachedCurrentUser();
    }

    sdkService.switchToConnectedClient();

    const authClient = sdkService.authClient!;

    // User just logged in, we fetch access token from the cookies
    if (cookies[COOKIES_KEY.ACCESS_TOKEN]) {
      authClient.setOauth2Tokens({
        accessToken: cookies[COOKIES_KEY.ACCESS_TOKEN],
        expiresIn: Number(cookies[COOKIES_KEY.ACCESS_TOKEN_EXPIRES_IN]),
        scopes: scopes.CONNECTED,
        grantType: 'implicit',
      });

      [COOKIES_KEY.ACCESS_TOKEN, COOKIES_KEY.ACCESS_TOKEN_EXPIRES_IN].forEach((cookieKey) =>
        deleteCookie(cookieKey)
      );
    }

    // User is supposed to be connected but we don't have an access token: we request a new one
    if (!authClient.hasAccessToken()) {
      pendingInitSdkPromise = authClient
        .setOauth2Tokens({ grantType: 'implicit', scopes: scopes.CONNECTED })
        .refreshImplicitToken();

      return pendingInitSdkPromise;
    }
  } else {
    if (cachedCurrentUser) {
      // User is supposed to be anonymous but our local cache contains one.
      // Let's remove it from our local cache
      await clearCachedCurrentUser();
    }

    pendingInitSdkPromise = sdkService.switchToAnonymousClient();
    return pendingInitSdkPromise;
  }

  return undefined;
}
